import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { BellRing, Loader2 } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75' };
const MODES = [
  { id: 'instant', label: 'Instant', desc: 'an email at each milestone' },
  { id: 'digest', label: 'Daily digest', desc: 'one summary email per day' },
  { id: 'off', label: 'Off', desc: 'no claim emails' },
];

export const NotificationPrefsCard = () => {
  const api = useApi();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/broker/notification-prefs').then(r => setPrefs(r.data)).catch(() => {});
  }, [api]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put('/broker/notification-prefs', patch);
      setPrefs(p => ({ ...p, ...r.data }));
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save preferences');
    } finally { setSaving(false); }
  };

  if (!prefs) return null;
  const labels = prefs.milestone_labels || {};
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="notification-prefs-card">
      <div className="flex items-center gap-2 mb-1">
        <BellRing className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Claim Email Preferences</h3>
        {saving && <Loader2 className="w-3 h-3 animate-spin" style={{ color: C.slate }} />}
      </div>
      <p className="text-[11px] mb-3" style={{ color: C.slate }}>Choose how ARIA keeps you posted on your clients' claims.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {MODES.map(m => (
          <button key={m.id} onClick={() => save({ mode: m.id })} disabled={saving}
            data-testid={`prefs-mode-${m.id}`}
            className="px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors disabled:opacity-60"
            style={prefs.mode === m.id
              ? { background: C.navy, color: '#FFF' }
              : { background: '#FFF', color: C.slate, border: '1px solid rgba(136,150,168,0.3)' }}>
            {m.label}
            <span className="block text-[9px] font-normal" style={{ color: prefs.mode === m.id ? 'rgba(255,255,255,0.7)' : C.slate }}>{m.desc}</span>
          </button>
        ))}
      </div>
      {prefs.mode !== 'off' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5" data-testid="prefs-milestones">
          {Object.entries(labels).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: C.navy }}>
              <input type="checkbox" checked={prefs.milestones?.[k] !== false} disabled={saving}
                data-testid={`prefs-milestone-${k}`}
                onChange={e => save({ milestones: { ...prefs.milestones, [k]: e.target.checked } })} />
              {label}
            </label>
          ))}
        </div>
      )}
    </Card>
  );
};
